import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const TermsConditionsPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Terms & Conditions | RESILIGENT Nigeria"
        description="Terms and conditions for RESILIGENT services in Nigeria and Africa. Governing terms for leadership development, executive coaching, and advisory services."
        keywords="terms conditions Nigeria, service agreement Lagos, RESILIGENT terms"
        canonical="/terms-conditions"
      />
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="bg-[#0a0a0a] pt-32 pb-16">
          <div className="w-full max-w-6xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="text-white font-semibold text-3xl sm:text-4xl lg:text-5xl leading-tight mb-6">
                Terms & Conditions
              </h1>
              <p className="text-slate-400 text-base lg:text-lg leading-relaxed">
                Last updated: January 2025
              </p>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="bg-white py-16 lg:py-20">
          <div className="w-full max-w-4xl mx-auto px-6 lg:px-8">
            <div className="prose prose-slate max-w-none">
              
              {/* Introduction */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  1. Introduction
                </h2>
                <p className="text-slate-600 text-base leading-relaxed mb-4">
                  These Terms and Conditions ("Terms") govern your use of the RESILIGENT website and our services. By accessing our website or engaging our services, you agree to be bound by these Terms.
                </p>
                <p className="text-slate-600 text-base leading-relaxed">
                  RESILIGENT provides advisory-led leadership and organisational development services, including diagnostics, coaching, capability building, and the Impact AS Plus™ platform.
                </p>
              </div>

              {/* Definitions */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  2. Definitions
                </h2>
                <ul className="space-y-3 mb-4">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">
                      <strong>"RESILIGENT", "we", "our", "us"</strong> refers to RESILIGENT and its affiliated entities.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">
                      <strong>"Client", "you", "your"</strong> refers to any individual or organisation using our website or services.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">
                      <strong>"Services"</strong> refers to all advisory, coaching, diagnostic, assessment, and development services provided by RESILIGENT.
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">
                      <strong>"Impact AS Plus™"</strong> refers to our proprietary leadership and talent intelligence system.
                    </span>
                  </li>
                </ul>
              </div>

              {/* Use of Website */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  3. Use of Website
                </h2>
                <p className="text-slate-600 text-base leading-relaxed mb-4">
                  You may use our website for lawful purposes only. You agree not to:
                </p>
                <ul className="space-y-3 mb-4">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Use the website in any way that breaches applicable laws or regulations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Attempt to gain unauthorised access to our systems or networks</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Transmit any harmful code, viruses, or malicious software</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Copy, reproduce, or redistribute our content without permission</span>
                  </li>
                </ul>
              </div>

              {/* Services */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  4. Our Services
                </h2>
                <p className="text-slate-600 text-base leading-relaxed mb-4">
                  RESILIGENT provides professional advisory and development services including:
                </p>
                <ul className="space-y-3 mb-4">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Leadership diagnostics and 360-degree assessments</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Executive coaching and leadership development</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Organisational advisory and change management</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Capability building and training programmes</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Graduate recruitment and assessment centre design</span>
                  </li>
                </ul>
                <p className="text-slate-600 text-base leading-relaxed">
                  Specific terms for individual engagements will be set out in separate service agreements or statements of work.
                </p>
              </div>

              {/* Intellectual Property */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  5. Intellectual Property
                </h2>
                <p className="text-slate-600 text-base leading-relaxed mb-4">
                  All intellectual property rights in our website, services, and materials belong to RESILIGENT or our licensors. This includes:
                </p>
                <ul className="space-y-3 mb-4">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">The RESILIGENT name, logo, and branding</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Impact AS Plus™ platform and associated diagnostics</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">JobFit™ and InsightEdge™ assessment tools</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">All methodologies, frameworks, and proprietary content</span>
                  </li>
                </ul>
                <p className="text-slate-600 text-base leading-relaxed">
                  You may not use, reproduce, or distribute our intellectual property without prior written consent.
                </p>
              </div>

              {/* Confidentiality */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  6. Confidentiality
                </h2>
                <p className="text-slate-600 text-base leading-relaxed mb-4">
                  We treat all client information with strict confidentiality. Assessment results, diagnostic data, and coaching conversations are handled in accordance with professional ethical standards and our Privacy Policy.
                </p>
                <p className="text-slate-600 text-base leading-relaxed">
                  Specific confidentiality provisions may be detailed in individual service agreements.
                </p>
              </div>

              {/* Professional Standards */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  7. Professional Standards
                </h2>
                <p className="text-slate-600 text-base leading-relaxed mb-4">
                  RESILIGENT operates in accordance with recognised professional and ethical standards. Our practitioners hold relevant qualifications and adhere to codes of conduct from bodies including:
                </p>
                <ul className="space-y-3 mb-4">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">International Coaching Federation (ICF)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">British Psychological Society (BPS)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Chartered Institute of Personnel and Development (CIPD)</span>
                  </li>
                </ul>
              </div>

              {/* Limitation of Liability */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  8. Limitation of Liability
                </h2>
                <p className="text-slate-600 text-base leading-relaxed mb-4">
                  To the fullest extent permitted by law:
                </p>
                <ul className="space-y-3 mb-4">
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Our website and its content are provided "as is" without warranties of any kind</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">We shall not be liable for any indirect, incidental, or consequential damages</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 bg-[#2693d0] rounded-full mt-2.5 flex-shrink-0" />
                    <span className="text-slate-600 text-base leading-relaxed">Our total liability for any claim shall not exceed the fees paid for the relevant services</span>
                  </li>
                </ul>
                <p className="text-slate-600 text-base leading-relaxed">
                  Nothing in these Terms excludes liability for death or personal injury caused by negligence, fraud, or any other liability that cannot be excluded by law.
                </p>
              </div>

              {/* Indemnification */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  9. Indemnification
                </h2>
                <p className="text-slate-600 text-base leading-relaxed">
                  You agree to indemnify and hold RESILIGENT harmless from any claims, damages, losses, or expenses arising from your breach of these Terms or misuse of our services.
                </p>
              </div>

              {/* Third Party Links */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  10. Third Party Links
                </h2>
                <p className="text-slate-600 text-base leading-relaxed">
                  Our website may contain links to third-party websites. We are not responsible for the content, privacy practices, or terms of any third-party sites. Access to such sites is at your own risk.
                </p>
              </div>

              {/* Termination */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  11. Termination
                </h2>
                <p className="text-slate-600 text-base leading-relaxed">
                  We reserve the right to suspend or terminate your access to our website or services if you breach these Terms. Termination of specific service engagements will be governed by the applicable service agreement.
                </p>
              </div>

              {/* Governing Law */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  12. Governing Law
                </h2>
                <p className="text-slate-600 text-base leading-relaxed mb-4">
                  These Terms shall be governed by and construed in accordance with the laws of England and Wales, unless otherwise specified in a service agreement.
                </p>
                <p className="text-slate-600 text-base leading-relaxed">
                  For clients based in Nigeria, Nigerian law may apply as specified in individual agreements.
                </p>
              </div>

              {/* Dispute Resolution */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  13. Dispute Resolution
                </h2>
                <p className="text-slate-600 text-base leading-relaxed">
                  Any disputes arising from these Terms or our services shall first be addressed through good-faith negotiation. If unresolved, disputes may be referred to mediation or arbitration as agreed between the parties.
                </p>
              </div>

              {/* Changes to Terms */}
              <div className="mb-12">
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  14. Changes to These Terms
                </h2>
                <p className="text-slate-600 text-base leading-relaxed">
                  We may update these Terms from time to time. Changes will be posted on this page with an updated revision date. Continued use of our website or services after changes constitutes acceptance of the revised Terms.
                </p>
              </div>

              {/* Contact */}
              <div>
                <h2 className="text-slate-800 font-semibold text-xl lg:text-2xl mb-4">
                  15. Contact Us
                </h2>
                <p className="text-slate-600 text-base leading-relaxed mb-4">
                  If you have questions about these Terms, please contact us:
                </p>
                <div className="bg-[#f0f5f8] rounded-xl p-6 border border-slate-100">
                  <p className="text-slate-700 text-base leading-relaxed mb-2">
                    <strong>Email:</strong> info@resiligent.com
                  </p>
                  <p className="text-slate-700 text-base leading-relaxed mb-2">
                    <strong>West Africa:</strong> 154 Prince Ade Odedina Street, Victoria Island, Lagos
                  </p>
                  <p className="text-slate-700 text-base leading-relaxed mb-2">
                    <strong>Phone (Nigeria):</strong> +234 (0)703 009 0712
                  </p>
                  <p className="text-slate-700 text-base leading-relaxed mb-2">
                    <strong>Europe:</strong> Monomark House, 27 Old Gloucester Street, WC1N 3AX, London UK
                  </p>
                  <p className="text-slate-700 text-base leading-relaxed">
                    <strong>Phone (UK):</strong> +44 (0)207 117 630 7
                  </p>
                </div>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TermsConditionsPage;
